import { Component, OnInit, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinSearchAccordionComponent } from '../../shared/design-system/search-accordion.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { EventHubApiService, EventHubCatalogListItem } from './event-hub-api.service';

@Component({
  standalone: true,
  selector: 'ain-event-hub-catalog',
  imports: [
    AinPageComponent,
    AinSearchAccordionComponent,
    AinStateComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    RouterLink
  ],
  template: `
    <ain-page [title]="t('eventHub.catalog.title')" [description]="t('eventHub.catalog.description')">
      <ain-search-accordion [title]="t('eventHub.catalog.filters.title')">
        <section class="search-grid">
          <label>
            <span>{{ t('eventHub.catalog.filters.eventTypePrefix') }}</span>
            <input pInputText [(ngModel)]="filters.eventTypePrefix" [placeholder]="t('eventHub.catalog.filters.eventTypePrefixPlaceholder')" />
          </label>
          <label>
            <span>{{ t('eventHub.catalog.filters.ownerModule') }}</span>
            <input pInputText [(ngModel)]="filters.ownerModuleKey" [placeholder]="t('eventHub.catalog.filters.ownerModulePlaceholder')" />
          </label>
          <label>
            <span>{{ t('columns.status') }}</span>
            <p-select
              [options]="statusOptions"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="filters.status"
              [showClear]="true"
              appendTo="body">
              <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
              <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
            </p-select>
          </label>
          <div class="actions">
            <button pButton type="button" [label]="t('platform.search.apply')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('platform.search.clear')" (click)="clearFilters()"></button>
          </div>
        </section>
      </ain-search-accordion>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.loadFailed')" [message]="error() ?? ''" />
      } @else if (!rows().length) {
        <ain-state type="empty" [title]="t('common.noRecords')" />
      } @else {
        <p-table [value]="rows()" [paginator]="true" [rows]="15" dataKey="id">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('eventHub.catalog.fields.eventType') }}</th>
              <th>{{ t('columns.version') }}</th>
              <th>{{ t('eventHub.catalog.fields.ownerModule') }}</th>
              <th>{{ t('columns.status') }}</th>
              <th>{{ t('eventHub.catalog.fields.description') }}</th>
              <th></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.eventType }}</td>
              <td>{{ row.version }}</td>
              <td>{{ row.ownerModuleKey }}</td>
              <td>{{ statusLabel(row) }}</td>
              <td>{{ row.description }}</td>
              <td>
                <a [routerLink]="['/event-hub/catalog', row.eventType, 'versions', row.version]">{{ t('common.open') }}</a>
              </td>
            </tr>
          </ng-template>
        </p-table>
      }
    </ain-page>
  `,
  styles: [`
    .search-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)) auto; gap: 10px; align-items: end; }
    .search-grid label { display: grid; gap: 6px; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; }
    a { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    @media (max-width: 1100px) { .search-grid { grid-template-columns: 1fr; } }
  `]
})
export class EventHubCatalogComponent implements OnInit {
  private readonly api = inject(EventHubApiService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);

  readonly rows = signal<EventHubCatalogListItem[]>([]);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);

  filters = {
    eventTypePrefix: '',
    ownerModuleKey: '',
    status: ''
  };

  readonly statusOptions = [
    { value: 'Active', labelKey: 'eventHub.catalog.status.active' },
    { value: 'Deprecated', labelKey: 'eventHub.catalog.status.deprecated' },
    { value: 'Retired', labelKey: 'eventHub.catalog.status.retired' }
  ];

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.listCatalog({
      eventTypePrefix: this.filters.eventTypePrefix.trim() || undefined,
      ownerModuleKey: this.filters.ownerModuleKey.trim() || undefined,
      status: this.filters.status || undefined
    }).subscribe({
      next: items => {
        this.rows.set(items);
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.error.set(this.t('eventHub.catalog.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  clearFilters(): void {
    this.filters = { eventTypePrefix: '', ownerModuleKey: '', status: '' };
    this.load();
  }

  statusLabel(row: EventHubCatalogListItem): string {
    const status = typeof row.status === 'number'
      ? ['Active', 'Deprecated', 'Retired'][row.status] ?? String(row.status)
      : String(row.status);
    return this.t(`eventHub.catalog.status.${status.toLowerCase()}`);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
